import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { checkPaymentStatus, retryPayment, cancelPayment } from '../../services/paymentService';
import { getOrderById } from '../../services/orderService';
import { COLORS, FONTS } from '../../theme/tokens';
import { PANEL_SX, PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from '../../theme/v2Styles';
import { useTranslation } from 'react-i18next';

const OrderCancel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get('order_id');
  const stripeSession = queryParams.get('stripe_session');
  
  // Store Stripe session ID in localStorage for payment service to use it
  useEffect(() => {
    if (stripeSession) {
      localStorage.setItem('stripe_session_id', stripeSession);
    }
  }, [stripeSession]);
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  useEffect(() => {
    // If no orderId in URL, redirect to home
    if (!orderId) {
      navigate('/');
      return;
    }
    
    const checkOrder = async () => {
      try {
        setLoading(true);
        
        // First check payment status - pass stripe session ID directly if available
        const paymentStatus = await checkPaymentStatus(orderId, stripeSession);
        
        // Get the order details to show in the UI
        const orderDetails = await getOrderById(orderId);
        setOrder(orderDetails);
        
        // If payment actually succeeded but user was redirected here,
        // we should redirect them to success page
        if (paymentStatus.status === 'succeeded' || 
            paymentStatus.status === 'paid_with_gift_card') {
          navigate(`/order-success?order_id=${orderId}`);
          return;
        }

        // // Automatically mark the order as cancelled - REMOVED
        // // The order status should remain 'Created' if the initial payment is cancelled.
        // if (stripeSession) {
        //   try {
        //     setCancelling(true);
        //     await cancelPayment(orderId);
        //     console.log('Order marked as cancelled in the system');
        //
        //     // If user cancels, we should NOT clear their cart items (they might want to try again)
        //     // Instead, just make sure the cart count is up to date by triggering a refresh
        //     window.dispatchEvent(new Event('cartUpdated'));
        //   } catch (cancelError) {
        //     console.error('Error cancelling payment in system:', cancelError);
        //     // Don't stop the flow - we still want to show the cancel page
        //   } finally {
        //     setCancelling(false);
        //   }
        // }
      } catch (error) {
        setError(error.message || t('orders.statusCheckError'));
      } finally {
        setLoading(false);
      }
    };
    
    checkOrder();
  }, [orderId, navigate, stripeSession]);
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('orders.checkingStatus')}
        </Typography>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('orders.returnHome')}
        </Button>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
      <Paper elevation={0} sx={{ ...PANEL_SX, p: { xs: 3, md: 5 }, textAlign: 'center', bgcolor: COLORS.surfacePanel }}>
        <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(122,46,59,0.10)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ErrorOutlineIcon sx={{ fontSize: 34, color: COLORS.wine }} />
        </Box>
        
        <Typography component="h1" sx={{ fontFamily: FONTS.heading, fontWeight: 600, fontSize: { xs: '1.625rem', md: '2rem' }, mb: 1.5 }}>
          {t('orders.paymentCancelled')}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {t('orders.paymentCancelledBefore')}<strong>{order?.order_number}</strong>{t('orders.paymentCancelledAfter')}
        </Typography>
        
        <Typography variant="body1" paragraph>
          {t('orders.orderSavedNotice')}
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            sx={SECONDARY_BUTTON_SX}
          >
            {t('orders.continueShopping')}
          </Button>
          
          <Button 
            variant="contained" 
            onClick={async () => {
              try {
                setRetrying(true);
                setError(null);
                // Use the retryPayment function to redirect back to Stripe
                await retryPayment(orderId);
                // Note: The page will redirect to Stripe, so we don't need to handle success case here
              } catch (error) {
                console.error('Error retrying payment:', error);
                setError(error.message || t('orders.retryFailed'));
                setRetrying(false);
              }
            }}
            disabled={retrying}
            sx={PRIMARY_BUTTON_SX}
          >
            {retrying ? <CircularProgress size={24} /> : t('orders.tryAgain')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default OrderCancel;
